'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import {
  Search,
  BookOpen,
  PenTool,
  User,
  Coins,
  Settings,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUserStore } from '@/utils/util_states';
import { localizedPublicPath, stripPublicLocale } from '@/i18n/config';
import { getLanguageDataByCode } from '@/i18n/language-data';

// ─────────────────────────────────────────────
// Navigation definition
// ─────────────────────────────────────────────
type NavItem = {
  label: string;
  route: string;
  icon: React.ComponentType<{ className?: string }>;
};

type NavSection = {
  title: string;
  items: NavItem[];
};

// NAV_SECTIONS is computed inside the component to access langData

// ─────────────────────────────────────────────
// Sidebar Component (208px)
// ─────────────────────────────────────────────
export default function Sidebar({ languageCode }: { languageCode: string }) {
  const pathname = usePathname();
  const basePathname = stripPublicLocale(pathname);
  const { userProfile, isLogin } = useUserStore();
  const langData = getLanguageDataByCode(languageCode);
  const discoverRoute = localizedPublicPath('/discover', languageCode);

  const navSections: NavSection[] = [
    {
      title: langData.sidebar_investigation,
      items: [
        { label: langData.nav_discover, route: discoverRoute, icon: Search },
        { label: langData.nav_my_puzzles, route: '/puzzles', icon: BookOpen },
      ],
    },
    {
      title: langData.sidebar_case_files,
      items: [
        { label: langData.nav_create, route: '/create', icon: PenTool },
      ],
    },
    {
      title: langData.sidebar_account,
      items: [
        { label: langData.nav_profile, route: '/profile', icon: User },
        { label: langData.nav_recharge, route: '/recharge', icon: Coins },
        { label: langData.nav_settings, route: '/settings', icon: Settings },
      ],
    },
  ];

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 flex h-screen w-[208px] flex-col',
        'border-r border-[var(--sidebar-border)]',
        'bg-[var(--sidebar)]',
        'animate-[fadeIn_0.4s_ease-out]',
      )}
    >
      {/* ── Brand ── */}
      <div className="px-5 pt-7 pb-2">
        <Link href={discoverRoute} className="group flex items-center gap-2">
          <Image src="/app_icon_1024.png" alt="境谜" width={32} height={32} className="rounded-lg shrink-0" />
          <div>
            <div
              className="text-[18px] tracking-[1.8px] text-[var(--lamp)]"
              style={{ fontFamily: 'var(--font-playfair), "Playfair Display", serif' }}
            >
              {langData.login_brand}
            </div>
            <p
              className={cn(
                'text-[10px] italic text-[var(--muted-foreground)] transition-colors group-hover:text-[var(--lamp)]',
                languageCode.toLowerCase().startsWith('zh') ? 'tracking-[0.12em]' : 'tracking-normal',
              )}
              style={{ fontFamily: 'var(--font-playfair), "Playfair Display", serif' }}
            >
              {langData.sidebar_tagline}
            </p>
          </div>
        </Link>
      </div>

      {/* ── Divider ── */}
      <div className="mx-5 my-3 h-px bg-[var(--sidebar-border)]" />

      {/* ── Navigation ── */}
      <nav className="flex-1 overflow-y-auto px-2">
        {navSections.map((section) => (
          <div key={section.title} className="mb-5">
            {/* Section label */}
            <p
              className="mb-2 px-2.5 text-[10px] uppercase tracking-[1.7px] text-[var(--muted-foreground)]"
              style={{ fontFamily: 'var(--font-playfair), "Playfair Display", serif', fontStyle: 'italic' }}
            >
              {section.title}
            </p>

            {/* Items */}
            {section.items.map((item) => {
              const itemBaseRoute = stripPublicLocale(item.route);
              const isActive = basePathname === itemBaseRoute || basePathname.startsWith(itemBaseRoute + '/');
              const Icon = item.icon;

              return (
                <Link
                  key={item.route}
                  href={item.route}
                  className={cn(
                    'group relative flex items-center gap-2 rounded-sm px-2.5 py-2.5 text-[13px] transition-all duration-200',
                    isActive
                      ? 'bg-[var(--lamp-dim)] text-[var(--lamp)]'
                      : 'text-gray-500 hover:text-gray-300 hover:bg-[var(--sidebar-accent)]',
                  )}
                >
                  {/* Left accent bar */}
                  {isActive && (
                    <span className="absolute left-0 top-1/2 h-[60%] w-[2px] -translate-y-1/2 rounded-full bg-[var(--lamp)]" />
                  )}

                  <Icon
                    className={cn(
                      'h-[16px] w-[16px] shrink-0 transition-colors',
                      isActive ? 'text-[var(--lamp)]' : 'text-gray-500 group-hover:text-gray-300',
                    )}
                  />
                  <span
                    className="truncate"
                    style={{ fontFamily: 'var(--font-noto), "Noto Sans SC", sans-serif' }}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

    </aside>
  );
}
